import { useCallback } from 'react';
import { isMantineColorScheme, useComputedColorScheme, useMantineColorScheme, type MantineColorScheme, type MantineColorSchemeManager } from '@mantine/core';

/**
 * Appearance: "Match my phone" (auto, the default), "Light" or "Dark".
 *
 * The choice is stored under Mantine's usual key so a member's earlier choice survives the
 * redesign. Every storage access is wrapped in try/catch: private windows and blocked site data
 * simply fall back to "Match my phone". index.html applies the stored value before React loads so
 * the page never flashes the wrong scheme.
 */
export const COLOR_SCHEME_STORAGE_KEY = 'mantine-color-scheme-value';

export type ThemeChoiceValue = MantineColorScheme; // 'auto' | 'light' | 'dark'

export const THEME_CHOICES: { value: ThemeChoiceValue; label: string; hint: string }[] = [
  { value: 'auto', label: 'Match my phone', hint: 'Light or dark, the same as your phone' },
  { value: 'light', label: 'Light', hint: 'Dark text on a light background' },
  { value: 'dark', label: 'Dark', hint: 'Light text on a dark background' },
];

export function themeChoiceLabel(value: ThemeChoiceValue): string {
  return THEME_CHOICES.find((c) => c.value === value)?.label ?? 'Match my phone';
}

function readStored(defaultValue: MantineColorScheme): MantineColorScheme {
  try {
    const v = window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    return isMantineColorScheme(v) ? v : defaultValue;
  } catch {
    return defaultValue;
  }
}

/** Mantine colour-scheme manager backed by localStorage, safe when storage is unavailable. */
export function carpoolColorSchemeManager(): MantineColorSchemeManager {
  let onStorage: ((e: StorageEvent) => void) | null = null;
  return {
    get: (defaultValue) => (typeof window === 'undefined' ? defaultValue : readStored(defaultValue)),
    set: (value) => {
      try {
        window.localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, value);
      } catch {
        /* storage blocked: the choice lasts for this visit only */
      }
    },
    subscribe: (onUpdate) => {
      onStorage = (e: StorageEvent) => {
        try {
          if (e.storageArea === window.localStorage && e.key === COLOR_SCHEME_STORAGE_KEY && isMantineColorScheme(e.newValue)) onUpdate(e.newValue);
        } catch {
          /* ignore */
        }
      };
      window.addEventListener('storage', onStorage);
    },
    unsubscribe: () => {
      if (onStorage) window.removeEventListener('storage', onStorage);
      onStorage = null;
    },
    clear: () => {
      try {
        window.localStorage.removeItem(COLOR_SCHEME_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    },
  };
}

/**
 * The member's appearance choice plus the scheme actually showing.
 * `choice` is 'auto' | 'light' | 'dark'; `scheme` is 'light' | 'dark'.
 */
export function useThemeChoice() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme('light', { getInitialValueInEffect: false });
  const setChoice = useCallback((value: ThemeChoiceValue) => setColorScheme(value), [setColorScheme]);
  return { choice: colorScheme as ThemeChoiceValue, scheme, setChoice };
}
